import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, UserPlus, Building2 } from "lucide-react";
import { Button, Field, Input, Select } from "@/design/components";
import { useSearchProjectUsers, useAddProjectMember, useSearchLeadContractorCandidates } from "@/domain/useOnboarding";
import { useInvitableProjects, useActiveProfile } from "@/domain/hooks";
import type { SearchableUser, LeadContractorCandidate } from "@/domain/api";
import { roleLabelFor, CONTRACTOR_WHOLE_PROJECT_ROLES } from "@/domain/cascadeRoles";

// Roles that carry org-lead authority — picking one of these also attaches
// the chosen organisation as this project's (or sub-project's) lead
// contractor (server/helpers/cascade.js's applyLeadContractorFromRole), so
// the modal needs an org picker in addition to the person picker.
const ORG_LEAD_ROLES = ["prime_lead", "sub_lead"];

// "Add members" — for someone already on Impact Ledger, skip Invite's
// email/onboarding-link ceremony and add them straight to the project by
// searching their name or email. Distinct entry point from InviteModal
// (which is for people outside the platform); structured the same way
// (Radix Dialog shell, same Field/Input/Button primitives, same
// reset/submitted state pattern) but with a search-and-pick control instead
// of free-text email/name fields. V1 is single-select (one person per open)
// to bound scope — no batch-add yet, and no sub-project scope selector
// (TeamTab only ever opens this for the whole project it's showing).
export function AddMembersModal({
  open,
  onOpenChange,
  projectId,
  projectName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number | string;
  projectName?: string;
}) {
  const invitable = useInvitableProjects();
  const profile = useActiveProfile();
  const { addMember, isPending, error } = useAddProjectMember();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<SearchableUser | null>(null);
  const [role, setRole] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [orgQuery, setOrgQuery] = useState("");
  const [debouncedOrgQuery, setDebouncedOrgQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<LeadContractorCandidate | null>(null);

  // Small local debounce — no shared utility exists yet for this, and one
  // consumer doesn't justify adding one.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedOrgQuery(orgQuery), 250);
    return () => clearTimeout(t);
  }, [orgQuery]);

  const { data: results = [], isFetching } = useSearchProjectUsers(projectId, debouncedQuery);
  const isOrgLeadRole = ORG_LEAD_ROLES.includes(role);
  const { data: orgResults = [], isFetching: isOrgFetching } = useSearchLeadContractorCandidates(
    isOrgLeadRole ? projectId : undefined,
    debouncedOrgQuery,
  );

  const matchedProject = useMemo(
    () => invitable.find((p) => String(p.projectId) === String(projectId)),
    [invitable, projectId],
  );

  // Whole-project roles only (no sub-project scope here) — mirrors
  // InviteModal's isSubScope=false branch exactly.
  const roleOptions = useMemo(() => {
    const keys =
      profile.group === "contractor"
        ? CONTRACTOR_WHOLE_PROJECT_ROLES
        : matchedProject?.appointableRoles ?? [];
    return keys.map((key) => ({ key, label: roleLabelFor(key) }));
  }, [profile.group, matchedProject]);

  useEffect(() => {
    if (roleOptions.length === 0) {
      if (role) setRole("");
      return;
    }
    if (!roleOptions.some((o) => o.key === role)) setRole(roleOptions[0].key);
  }, [roleOptions, role]);

  // Switching away from an org-lead role leaves a stale org selection with
  // nothing to attach it to — clear it so it can't be silently submitted.
  useEffect(() => {
    if (!isOrgLeadRole) {
      setSelectedOrg(null);
      setOrgQuery("");
    }
  }, [isOrgLeadRole]);

  const canSend = !!selected && !!role && (!isOrgLeadRole || !!selectedOrg);

  function reset() {
    setQuery("");
    setDebouncedQuery("");
    setSelected(null);
    setRole("");
    setSubmitted(false);
    setAlreadyMember(false);
    setOrgQuery("");
    setDebouncedOrgQuery("");
    setSelectedOrg(null);
  }

  async function onSend() {
    if (!selected || !role) return;
    if (isOrgLeadRole && !selectedOrg) return;
    const result = await addMember(projectId, {
      userId: selected.id,
      role,
      organisationId: isOrgLeadRole ? selectedOrg!.id : undefined,
    });
    if (result) {
      setAlreadyMember(result.alreadyMember);
      setSubmitted(true);
    }
  }

  const roleLabel = roleOptions.find((r) => r.key === role)?.label ?? role;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Member added" : "Add members"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>{selected?.firstname} {selected?.lastname}</strong>{" "}
                  {alreadyMember ? "was already on this project" : "has been added to this project"} as {roleLabel}.
                  {selectedOrg && ` "${selectedOrg.name}" is now the lead contractor for ${projectName ?? "this project"}.`}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Add another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Add someone already on Impact Ledger straight to {projectName ?? "this project"} — no onboarding link needed.
              </Dialog.Description>

              <Field label="Search by name or email">
                <div className="relative">
                  <Input
                    value={selected ? `${selected.firstname} ${selected.lastname}` : query}
                    onChange={(e) => {
                      setSelected(null);
                      setQuery(e.target.value);
                    }}
                    placeholder="e.g. Grace Nansubuga"
                    autoComplete="off"
                  />
                  {!selected && debouncedQuery.trim().length >= 2 && (
                    <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-line bg-card shadow-overlay">
                      {isFetching ? (
                        <p className="px-3 py-2 text-sm text-faint">Searching…</p>
                      ) : results.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-faint">No matching platform users.</p>
                      ) : (
                        results.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="block w-full px-3 py-2 text-left hover:bg-subtle"
                            onClick={() => {
                              setSelected(u);
                              setQuery("");
                            }}
                          >
                            <p className="text-sm font-medium text-ink">{u.firstname} {u.lastname}</p>
                            <p className="text-xs text-faint">{u.email}</p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Role">
                <Select value={role} onChange={(e) => setRole(e.target.value)} disabled={roleOptions.length === 0}>
                  {roleOptions.length === 0 ? (
                    <option value="">No roles you can appoint here</option>
                  ) : (
                    roleOptions.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))
                  )}
                </Select>
              </Field>

              {isOrgLeadRole && (
                <Field label="Organisation">
                  <div className="relative">
                    <Input
                      value={selectedOrg ? selectedOrg.name : orgQuery}
                      onChange={(e) => {
                        setSelectedOrg(null);
                        setOrgQuery(e.target.value);
                      }}
                      placeholder="e.g. East African Wire & Cable Co."
                      autoComplete="off"
                    />
                    {!selectedOrg && debouncedOrgQuery.trim().length >= 2 && (
                      <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-line bg-card shadow-overlay">
                        {isOrgFetching ? (
                          <p className="px-3 py-2 text-sm text-faint">Searching…</p>
                        ) : orgResults.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-faint">No matching active organisations.</p>
                        ) : (
                          orgResults.map((o) => (
                            <button
                              key={o.id}
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-subtle"
                              onClick={() => {
                                setSelectedOrg(o);
                                setOrgQuery("");
                              }}
                            >
                              <Building2 className="h-4 w-4 shrink-0 text-faint" aria-hidden />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-ink">{o.name}</p>
                                <p className="truncate text-xs text-faint">{o.email}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </Field>
              )}

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSend || isPending} onClick={onSend}>
                <UserPlus className="h-4 w-4" /> {isPending ? "Adding…" : "Add member"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
